import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { api } from '../lib/api.ts';
import { parseUnifiedDiff, type ParsedDiffFile, type ParsedDiffLine } from '../lib/diff.ts';
import { relativeTime } from '../lib/hooks.ts';
import { Icon } from '../lib/icons.tsx';
import { displayHost } from '../lib/project.ts';
import type { GitBranch, GitChange, GitCommitDiff, GitDiff, GitLogRow, GitRef, GitSnapshot } from '../lib/protocol.ts';

export type GitSubTab = 'worktree' | 'history' | 'branches';

export function GitSubTabs({
  tab,
  dirty,
  onTab,
}: {
  tab: GitSubTab;
  dirty: boolean;
  onTab: (tab: GitSubTab) => void;
}): JSX.Element {
  return (
    <nav className="git-subtabs" role="tablist" aria-label="Git views">
      <button
        type="button"
        role="tab"
        id="tab-worktree"
        aria-selected={tab === 'worktree'}
        aria-controls="pane-worktree"
        className={`git-subtab${tab === 'worktree' ? ' git-subtab-on' : ''}`}
        onClick={() => onTab('worktree')}
      >
        <span className="git-subtab-label">
          Worktree
          {dirty && <span className="session-tab-dot session-tab-dot-dirty" aria-hidden="true" />}
        </span>
      </button>
      <button
        type="button"
        role="tab"
        id="tab-history"
        aria-selected={tab === 'history'}
        aria-controls="pane-history"
        className={`git-subtab${tab === 'history' ? ' git-subtab-on' : ''}`}
        onClick={() => onTab('history')}
      >
        History
      </button>
      <button
        type="button"
        role="tab"
        id="tab-branches"
        aria-selected={tab === 'branches'}
        aria-controls="pane-branches"
        className={`git-subtab${tab === 'branches' ? ' git-subtab-on' : ''}`}
        onClick={() => onTab('branches')}
      >
        Branches
      </button>
    </nav>
  );
}

/** Local `YYYY-MM-DD HH:MM:SS` — shown in the commit box and used if left as-is. */
function timestampMessage(now = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
}

export function GitView({
  projectId,
  snap,
  error,
  busy,
  hidden,
  subTab,
  onRefresh,
  onCommit,
  onPush,
  onSnapshot,
}: {
  projectId: string;
  snap: GitSnapshot | null;
  error: string | null;
  busy: boolean;
  hidden?: boolean;
  subTab: GitSubTab;
  onRefresh: () => void;
  onCommit: (message: string) => Promise<void>;
  onPush: () => Promise<void>;
  onSnapshot: (snap: GitSnapshot) => void;
}): JSX.Element {
  const [commitMessage, setCommitMessage] = useState(timestampMessage);
  const [commitError, setCommitError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [selected, setSelected] = useState<GitChange | null>(null);
  const [diff, setDiff] = useState<GitDiff | null>(null);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffBusy, setDiffBusy] = useState(false);
  const [branches, setBranches] = useState<GitBranch[]>([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchesError, setBranchesError] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkingOut, setCheckingOut] = useState<string | null>(null);
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [selectedHash, setSelectedHash] = useState<string | null>(null);
  const [commitDiff, setCommitDiff] = useState<GitCommitDiff | null>(null);
  const [commitDiffError, setCommitDiffError] = useState<string | null>(null);
  const [commitDiffBusy, setCommitDiffBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetting, setResetting] = useState<'soft' | 'hard' | null>(null);
  const [discardError, setDiscardError] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState<string | null>(null);
  const commitEdited = useRef(false);
  const dirty = snap?.dirty === true;
  const canPush = snap !== null && !snap.detached && snap.upstream !== null && snap.ahead > 0;
  const pushTitle = !snap
    ? 'Push'
    : snap.detached
      ? 'Detached HEAD'
      : !snap.upstream
        ? 'No upstream configured'
        : snap.ahead === 0
          ? 'Nothing to push'
          : `Push ${snap.ahead} commit${snap.ahead === 1 ? '' : 's'} to ${snap.upstream}`;

  useEffect(() => {
    setSelected(null);
    setDiff(null);
    setDiffError(null);
    setDiffBusy(false);
  }, [projectId, snap]);

  useEffect(() => {
    setSelectedHash(null);
    setCommitDiff(null);
    setCommitDiffError(null);
    setCommitDiffBusy(false);
    setResetError(null);
    setResetting(null);
    setDiscardError(null);
    setDiscarding(null);
  }, [projectId]);

  useEffect(() => {
    if (!selectedHash) {
      setCommitDiff(null);
      setCommitDiffError(null);
      setCommitDiffBusy(false);
      return;
    }
    let cancelled = false;
    setCommitDiff(null);
    setCommitDiffError(null);
    setCommitDiffBusy(true);
    void api
      .gitCommitDiff(projectId, selectedHash)
      .then((next) => {
        if (!cancelled) setCommitDiff(next);
      })
      .catch((err) => {
        if (!cancelled) setCommitDiffError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setCommitDiffBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, selectedHash]);

  const loadBranches = useCallback(async (): Promise<void> => {
    setBranchesLoading(true);
    setBranchesError(null);
    try {
      const result = await api.gitBranches(projectId);
      setBranches(result.branches);
    } catch (err) {
      setBranchesError(err instanceof Error ? err.message : String(err));
    } finally {
      setBranchesLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (subTab !== 'branches') return;
    void loadBranches();
  }, [subTab, loadBranches]);

  const openDiff = async (change: GitChange): Promise<void> => {
    const same =
      selected?.path === change.path &&
      (selected?.origPath ?? '') === (change.origPath ?? '');
    if (same) {
      setSelected(null);
      setDiff(null);
      setDiffError(null);
      return;
    }
    setSelected(change);
    setDiff(null);
    setDiffError(null);
    setDiffBusy(true);
    try {
      setDiff(await api.gitDiff(projectId, change.path));
    } catch (err) {
      setDiffError(err instanceof Error ? err.message : String(err));
    } finally {
      setDiffBusy(false);
    }
  };

  const commit = async (): Promise<void> => {
    if (committing || !dirty) return;
    const message = (commitEdited.current ? commitMessage.trim() : '') || timestampMessage();
    setCommitting(true);
    setCommitError(null);
    try {
      await onCommit(message);
      commitEdited.current = false;
      setCommitMessage(timestampMessage());
    } catch (err) {
      setCommitError(err instanceof Error ? err.message : String(err));
    } finally {
      setCommitting(false);
    }
  };

  const push = async (): Promise<void> => {
    if (pushing || !canPush) return;
    setPushing(true);
    setPushError(null);
    try {
      await onPush();
    } catch (err) {
      setPushError(err instanceof Error ? err.message : String(err));
    } finally {
      setPushing(false);
    }
  };

  const checkout = async (branchName: string): Promise<void> => {
    if (checkingOut || creatingBranch) return;
    if (snap && !snap.detached && snap.branch === branchName) return;
    setCheckingOut(branchName);
    setCheckoutError(null);
    try {
      await api.checkoutBranch(projectId, branchName);
      void onRefresh();
      void loadBranches();
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : String(err));
    } finally {
      setCheckingOut(null);
    }
  };

  const toggleCommit = (hash: string): void => {
    setResetError(null);
    setSelectedHash((current) => (current === hash ? null : hash));
  };

  const resetTo = async (mode: 'soft' | 'hard'): Promise<void> => {
    if (!selectedHash || resetting) return;
    setResetting(mode);
    setResetError(null);
    try {
      onSnapshot(await api.reset(projectId, selectedHash, mode));
      setSelectedHash(null);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : String(err));
    } finally {
      setResetting(null);
    }
  };

  const discard = async (change: GitChange): Promise<void> => {
    if (discarding) return;
    setDiscarding(change.path);
    setDiscardError(null);
    try {
      onSnapshot(await api.discardFile(projectId, change.path));
    } catch (err) {
      setDiscardError(err instanceof Error ? err.message : String(err));
    } finally {
      setDiscarding(null);
    }
  };

  const createNewBranch = async (): Promise<void> => {
    if (creatingBranch || checkingOut) return;
    setCreatingBranch(true);
    setCheckoutError(null);
    try {
      await api.createBranch(projectId);
      void onRefresh();
      await loadBranches();
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreatingBranch(false);
    }
  };

  return (
    <div className="git-pane" id="pane-git" role="tabpanel" aria-labelledby="tab-git" hidden={hidden}>
      <div
        className="git-body git-body-fabs"
        id="pane-worktree"
        role="tabpanel"
        aria-labelledby="tab-worktree"
        hidden={subTab !== 'worktree'}
      >
        {error && <div className="note note-error">{error}</div>}
        {busy && !snap && (
          <div className="thinking">
            <span className="spinner" /> reading repository
          </div>
        )}
        {snap && (
          <section className="git-section">
            {snap.changes.length === 0 ? (
              <div className="git-clean">Clean</div>
            ) : (
              <ul className="git-changes">
                {snap.changes.map((change) => {
                  const open =
                    selected?.path === change.path &&
                    (selected?.origPath ?? '') === (change.origPath ?? '');
                  return (
                    <li key={`${change.xy}:${change.origPath ?? ''}:${change.path}`}>
                      <ChangeRow
                        change={change}
                        open={open}
                        discarding={discarding === change.path}
                        discardDisabled={discarding !== null}
                        onOpen={() => void openDiff(change)}
                        onDiscard={() => void discard(change)}
                      />
                      {open && (
                        <DiffPanel diff={diff} busy={diffBusy} error={diffError} truncated={diff?.truncated} />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {snap.changesTruncated && <div className="git-more">and more unshown files</div>}
          </section>
        )}
      </div>

      <div
        className="git-body"
        id="pane-history"
        role="tabpanel"
        aria-labelledby="tab-history"
        hidden={subTab !== 'history'}
      >
        {error && <div className="note note-error">{error}</div>}
        {snap?.syncError && <div className="note note-error">{snap.syncError}</div>}
        {busy && !snap && (
          <div className="thinking">
            <span className="spinner" /> reading repository
          </div>
        )}
        {snap &&
          (snap.log.length === 0 ? (
            <div className="git-clean">No commits yet</div>
          ) : (
            <div className="git-log">
              {snap.log.map((row, i) => {
                const hash = row.commit?.hash;
                return (
                  <LogRow
                    key={hash ?? `g${i}`}
                    row={row}
                    selected={hash === selectedHash}
                    resetting={resetting}
                    diff={hash === selectedHash ? commitDiff : null}
                    diffBusy={hash === selectedHash && commitDiffBusy}
                    diffError={hash === selectedHash ? commitDiffError : null}
                    onSelect={hash ? () => toggleCommit(hash) : undefined}
                    onReset={(mode) => void resetTo(mode)}
                  />
                );
              })}
            </div>
          ))}
        {resetError && (
          <div className="note note-error" aria-live="polite">
            {resetError}
          </div>
        )}
      </div>

      <div
        className="git-body git-body-fab"
        id="pane-branches"
        role="tabpanel"
        aria-labelledby="tab-branches"
        hidden={subTab !== 'branches'}
      >
        {branchesError && <div className="note note-error">{branchesError}</div>}
        {branchesLoading && (
          <div className="thinking">
            <span className="spinner" /> loading branches
          </div>
        )}
        {!branchesLoading && branches.length === 0 && (
          <div className="git-clean">No branches</div>
        )}
        {!branchesLoading && branches.length > 0 && (
          <section className="git-section">
            <div className="git-branches-list">
              {branches.map((branch) => {
                const current = snap?.detached !== true && snap?.branch === branch.name;
                return (
                  <button
                    key={branch.name}
                    type="button"
                    className={`git-branch-row${current ? ' git-branch-current' : ''}`}
                    onClick={() => void checkout(branch.name)}
                    disabled={checkingOut !== null || creatingBranch || current}
                    aria-current={current ? 'true' : undefined}
                    aria-label={current ? `Current branch ${branch.name}` : `Checkout branch ${branch.name}`}
                  >
                    <span
                      className={`git-branch-current-indicator${current ? '' : ' git-branch-current-indicator-off'}`}
                      aria-hidden="true"
                    />
                    <span className="git-branch-name">{branch.name}</span>
                    {checkingOut === branch.name && <span className="spinner" />}
                  </button>
                );
              })}
            </div>
          </section>
        )}
        {checkoutError && (
          <div className="note note-error" aria-live="polite">
            {checkoutError}
          </div>
        )}
      </div>

      {subTab === 'branches' && (
        <div className="fabs fabs-above-subtabs">
          <button
            type="button"
            className="fab"
            onClick={() => void createNewBranch()}
            disabled={creatingBranch || checkingOut !== null || !projectId}
            aria-label="New branch"
            title="Create and check out a new branch"
          >
            <Icon name="branchPlus" size={22} strokeWidth={2.4} />
          </button>
        </div>
      )}

      {subTab === 'worktree' && (
        <>
          <div className="fabs">
            <button
              type="button"
              className="fab fab-quiet"
              onClick={() => void push()}
              disabled={pushing || !canPush}
              aria-label={
                snap && snap.ahead > 0
                  ? `Push ${snap.ahead} commit${snap.ahead === 1 ? '' : 's'}`
                  : 'Push'
              }
              title={pushTitle}
            >
              <Icon name="push" size={22} strokeWidth={2.4} />
              {snap && snap.ahead > 0 && (
                <span className="fab-badge" aria-hidden="true">
                  {snap.ahead}
                </span>
              )}
            </button>
            <button
              type="button"
              className="fab"
              onClick={() => void commit()}
              disabled={committing || !dirty}
              aria-label="Commit"
              title={!dirty ? 'Nothing to commit' : 'Commit all changes'}
            >
              <Icon name="commit" size={22} strokeWidth={2.4} />
            </button>
          </div>
          <div className="commit-bar">
            <div className="commit-row">
              <input
                type="text"
                value={commitMessage}
                onChange={(e) => {
                  commitEdited.current = true;
                  setCommitMessage(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void commit();
                  }
                }}
                placeholder="Commit message"
                aria-label="Commit message"
                autoCapitalize="sentences"
                autoCorrect="on"
                spellCheck
                enterKeyHint="done"
              />
            </div>
            {(commitError || pushError || discardError || snap?.desktopError || snap?.syncError) && (
              <div className="composer-note" aria-live="polite">
                {commitError ?? pushError ?? discardError ?? snap?.desktopError ?? snap?.syncError}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ChangeRow({
  change,
  open,
  discarding,
  discardDisabled,
  onOpen,
  onDiscard,
}: {
  change: GitChange;
  open: boolean;
  discarding: boolean;
  discardDisabled: boolean;
  onOpen: () => void;
  onDiscard: () => void;
}): JSX.Element {
  const code = changeCode(change.xy);
  return (
    <div className={`git-change-row${open ? ' git-change-open' : ''}`}>
      <button
        type="button"
        className="git-change"
        onClick={onOpen}
        aria-expanded={open}
        aria-label={`View diff for ${change.path}`}
      >
        <span className={`git-xy git-xy-${code === '?' ? 'untracked' : code}`}>{code}</span>
        <span className="git-path">
          {change.origPath ? (
            <>
              <span className="git-path-old">{change.origPath}</span>
              {' → '}
              {change.path}
            </>
          ) : (
            change.path
          )}
        </span>
        <ChangeStats change={change} />
      </button>
      <button
        type="button"
        className="git-change-undo"
        onClick={onDiscard}
        disabled={discardDisabled}
        aria-label={`Undo changes to ${change.path}`}
        title={`Undo changes to ${change.path}`}
      >
        {discarding ? <span className="spinner" /> : <Icon name="undo" size={18} strokeWidth={2.2} />}
      </button>
    </div>
  );
}

function ChangeStats({ change }: { change: GitChange }): JSX.Element | null {
  const { additions, deletions } = change;
  if (additions === undefined && deletions === undefined) return null;
  const showAdd = additions !== undefined && additions > 0;
  const showDel = deletions !== undefined && deletions > 0;
  if (!showAdd && !showDel) return null;
  return (
    <span className="git-stats" aria-hidden="true">
      {showAdd && <span className="git-stat-add">+{additions}</span>}
      {showDel && <span className="git-stat-del">-{deletions}</span>}
    </span>
  );
}

/** Header +/- for the selected project's working tree. Hidden when clean. */
export function Diffstat({ stat }: { stat: { additions: number; deletions: number } | null }): JSX.Element | null {
  if (!stat) return null;
  const { additions, deletions } = stat;
  if (additions <= 0 && deletions <= 0) return null;
  return (
    <span
      className="topbar-diffstat"
      aria-label={`${additions} added, ${deletions} deleted`}
    >
      {additions > 0 && <span className="git-stat-add">+{additions}</span>}
      {deletions > 0 && <span className="git-stat-del">-{deletions}</span>}
    </span>
  );
}

function DiffPanel({
  diff,
  busy,
  error,
  truncated,
}: {
  diff: { patch: string; truncated?: boolean } | null;
  busy: boolean;
  error: string | null;
  truncated?: boolean;
}): JSX.Element {
  const files = diff ? parseUnifiedDiff(diff.patch) : [];
  const cut = truncated ?? diff?.truncated;
  return (
    <div className="git-diff-panel" aria-live="polite">
      {busy && (
        <div className="git-diff-status">
          <span className="spinner" /> loading diff
        </div>
      )}
      {error && <div className="note note-error">{error}</div>}
      {!busy && !error && diff && (
        <>
          {cut && <div className="git-diff-truncated">Diff truncated on the server</div>}
          {files.length === 0 ? (
            <div className="git-diff-status">No textual diff</div>
          ) : (
            <FormattedDiff files={files} />
          )}
        </>
      )}
    </div>
  );
}

function FormattedDiff({ files }: { files: ParsedDiffFile[] }): JSX.Element {
  return (
    <div className="git-diff">
      {files.map((file, i) => (
        <section key={`${file.path}:${i}`} className="git-diff-file">
          <header className="git-diff-file-head">
            <span className="git-diff-file-path">
              {file.origPath ? (
                <>
                  <span className="git-path-old">{file.origPath}</span>
                  {' → '}
                  {file.path}
                </>
              ) : (
                file.path
              )}
            </span>
            {file.status && (
              <span className={`git-diff-file-status git-diff-file-status-${file.status}`}>{file.status}</span>
            )}
          </header>
          <div className="git-diff-lines">
            {file.lines.map((line, j) => (
              <DiffLineView key={j} line={line} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function DiffLineView({ line }: { line: ParsedDiffLine }): JSX.Element {
  const sign = line.kind === 'add' ? '+' : line.kind === 'del' ? '-' : line.kind === 'ctx' ? ' ' : '';
  return (
    <div className={`git-diff-line git-diff-line-${line.kind}`}>
      <span className="git-diff-sign" aria-hidden="true">
        {sign}
      </span>
      <span className="git-diff-text">{line.text}</span>
    </div>
  );
}

function LogRow({
  row,
  selected,
  resetting,
  diff,
  diffBusy,
  diffError,
  onSelect,
  onReset,
}: {
  row: GitLogRow;
  selected: boolean;
  resetting: 'soft' | 'hard' | null;
  diff: GitCommitDiff | null;
  diffBusy: boolean;
  diffError: string | null;
  onSelect?: () => void;
  onReset: (mode: 'soft' | 'hard') => void;
}): JSX.Element {
  const commit = row.commit;
  return (
    <div
      className={`git-row${commit?.head ? ' git-row-head' : ''}${commit ? '' : ' git-row-edge'}${selected ? ' git-row-selected' : ''}`}
    >
      <Graph text={row.graph} head={commit?.head ?? false} />
      {commit && onSelect && (
        <div className="git-commit">
          <button
            type="button"
            className={`git-commit-select${selected ? ' git-commit-select-on' : ''}`}
            onClick={onSelect}
            aria-pressed={selected}
            aria-expanded={selected}
            aria-label={`View commit ${commit.shortHash} ${commit.subject}`}
          >
            <div className="git-subject-line">
              {commit.refs.map((gitRef) => (
                <RefPill key={`${gitRef.kind}:${gitRef.name}`} gitRef={gitRef} />
              ))}
              <span className="git-subject">{commit.subject}</span>
            </div>
            <div className="git-meta">
              <span className="git-hash">{commit.shortHash}</span>
              {commit.author}
              {' · '}
              {relativeTime(commit.timestamp)}
            </div>
          </button>
          {selected && (
            <>
              <div className="git-reset-actions git-reset-actions-sm">
                <button
                  type="button"
                  className="git-reset-btn git-reset-btn-sm"
                  onClick={() => onReset('soft')}
                  disabled={resetting !== null}
                  title="Move HEAD here and keep all file changes"
                >
                  {resetting === 'soft' && <span className="spinner" />}
                  Soft
                </button>
                <button
                  type="button"
                  className="git-reset-btn git-reset-btn-sm git-reset-btn-hard"
                  onClick={() => onReset('hard')}
                  disabled={resetting !== null}
                  title="Move HEAD here and discard uncommitted changes"
                >
                  {resetting === 'hard' && <span className="spinner" />}
                  Hard
                </button>
              </div>
              <DiffPanel diff={diff} busy={diffBusy} error={diffError} truncated={diff?.truncated} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Graph({ text, head }: { text: string; head: boolean }): JSX.Element {
  return (
    <span className="git-graph" aria-hidden="true">
      {[...text].map((ch, i) =>
        ch === '*' ? (
          <span key={i} className={head ? 'git-star git-star-head' : 'git-star'}>
            *
          </span>
        ) : (
          ch
        ),
      )}
    </span>
  );
}

function RefPill({ gitRef }: { gitRef: GitRef }): JSX.Element {
  return <span className={`git-ref git-ref-${gitRef.kind}`}>{gitRef.name}</span>;
}

export function gitSubtitle(snap: GitSnapshot): string {
  const name = snap.detached ? 'HEAD' : snap.branch;
  const branch = snap.dirty ? `${name}*` : name;
  if (!snap.activeHost) return branch;
  return `${branch} · ${displayHost(snap.activeHost)}`;
}

function changeCode(xy: string): string {
  if (xy === '??') return '?';
  if (xy.includes('U') || xy === 'AA' || xy === 'DD') return 'U';
  if (xy.includes('R')) return 'R';
  if (xy.includes('C')) return 'C';
  if (xy.includes('D') && !xy.includes('A')) return 'D';
  if (xy.includes('A')) return 'A';
  if (xy.includes('M')) return 'M';
  return (xy.trim()[0] ?? '?').toUpperCase();
}